'use client';

import { useState } from 'react';
import { Mail, Smartphone, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { WhatsAppIcon } from './BrandIcons';

interface NewsletterSignupProps {
  title?: string;
  description?: string;
  phone?: string;
  compact?: boolean;
}

export default function NewsletterSignup({
  title = "Restez informé",
  description = "Recevez nos actualités, événements et conseils éducatifs directement dans votre boîte mail",
  phone = "22870013605",
  compact = false
}: NewsletterSignupProps) {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    
    setIsSubmitting(true);
    setStatus('idle');
    setErrorMessage('');

    try {
      const res = await fetch('/api/newsletter', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Une erreur est survenue.');
      }

      setStatus('success');
      setEmail('');
    } catch (err) {
      setStatus('error');
      setErrorMessage(err instanceof Error ? err.message : 'Une erreur est survenue.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (compact) {
    return (
      <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
        <h4 className="text-sm font-semibold text-white mb-2">{title}</h4>
        <p className="text-xs text-blue-100 mb-3 leading-relaxed">{description}</p>

        {status === 'success' ? (
          <div className="bg-green-500/20 border border-green-500/30 rounded-lg p-3 text-center">
            <CheckCircle2 className="h-5 w-5 text-green-400 mx-auto mb-1" />
            <p className="text-sm text-green-100 font-medium">Inscription réussie !</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-3">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Votre email"
              required
              className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/60 text-sm focus:ring-2 focus:ring-white focus:border-white backdrop-blur-sm"
              disabled={isSubmitting}
            />
            {status === 'error' && (
              <p className="text-xs text-red-300 flex items-start gap-1">
                <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                {errorMessage}
              </p>
            )}
            <button
              type="submit"
              disabled={isSubmitting || !email}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50 text-sm"
            >
              {isSubmitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Mail className="h-4 w-4" />
              )}
              {isSubmitting ? 'Inscription...' : 'S\'inscrire'}
            </button>
          </form>
        )}
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-r from-c2e-blue to-c2e-blue-light rounded-2xl p-8 text-white text-center">
      <div className="max-w-md mx-auto">
        <div className="mb-6">
          <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Mail className="h-8 w-8" aria-hidden="true" />
          </div>
          <h3 className="text-2xl font-bold mb-2">{title}</h3>
          <p className="text-blue-100">{description}</p>
        </div>

        {status === 'success' ? (
          <div className="bg-green-500/20 border border-green-500/30 rounded-xl p-6 text-center animate-in fade-in zoom-in duration-300">
            <CheckCircle2 className="h-10 w-10 text-green-400 mx-auto mb-3" />
            <h4 className="text-xl font-bold text-white mb-2">Merci pour votre inscription !</h4>
            <p className="text-green-100">Vous recevrez bientôt nos prochaines actualités.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Votre email"
                required
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/70 focus:ring-2 focus:ring-white focus:border-transparent backdrop-blur-sm"
                disabled={isSubmitting}
              />
              {status === 'error' && (
                <p className="text-sm text-red-300 mt-2 flex items-start justify-center gap-1.5">
                  <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                  {errorMessage}
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={isSubmitting || !email}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {isSubmitting ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <Mail className="h-5 w-5" />
              )}
              {isSubmitting ? 'Inscription en cours...' : 'Rejoindre la newsletter'}
            </button>
          </form>
        )}

        <p className="text-xs text-blue-200 mt-6 flex items-center justify-center gap-1.5">
          <Smartphone className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <a 
            href={`https://wa.me/${phone}`} 
            target="_blank" 
            rel="noopener noreferrer"
            className="hover:text-white transition-colors flex items-center gap-1"
          >
            Préférez-vous nous contacter sur WhatsApp ? <WhatsAppIcon className="h-3 w-3 inline" />
          </a>
        </p>
      </div>
    </div>
  );
}
